import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Sidebar.scss";
import bellringing from "../../Assets/bell-ringing.png"
import onofbtn from "../../Assets/on-of-btn.png"
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { logout } from "../../Features/Auth/authSlice";
import { getAdminSocket } from "../../Api/socket";
import {
  playNotificationSound,
  primeNotificationAudioFromUserGesture,
} from "../../utils/playNotificationSound";
import {
  getAdminNotificationPath,
  isAdminNotificationInteractive,
} from "../../utils/notificationRoutes";
import { isNotificationUnread } from "../../utils/notificationUnread";
import { showAlert, showConfirm, setGlobalLoading } from "../../feedback/feedbackApi";
import {
  FiGrid,
  FiMapPin,
  FiUsers,
  FiUserCheck,
  FiHome,
  FiClipboard,
  FiFileText,
  FiActivity,
  FiLayers,
  FiBell,
  FiSettings,
  FiImage,
  FiMessageCircle,
  FiChevronRight,
} from "react-icons/fi";

const VIEWED_STORAGE_KEY = "healbid_admin_notifications_viewed_at";

/** Build toast copy from socket payload or generic fallback */
function toastFromSocketPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return {
      title: "New notification",
      body: "You have a new update. Open the bell to see details.",
    };
  }
  const titleRaw = payload.title || payload.type || "Notification";
  const title = typeof titleRaw === "string" ? titleRaw : String(titleRaw);
  if (payload.body && String(payload.body).trim()) {
    return { title, body: String(payload.body).trim() };
  }
  const t = String(payload.type || "").toLowerCase();
  if (t === "bid_update") {
    return {
      title: "Bid activity",
      body: "There was an update on a bid. Check the dashboard or notifications list.",
    };
  }
  if (t === "reverse_bid" || t === "offer_accepted" || t === "bid_rejected") {
    return {
      title,
      body: "Open Notifications for the full message.",
    };
  }
  if (t === "prescription_upload" || t === "prescription_pending" || t === "prescription_resubmitted") {
    return {
      title,
      body: payload.body
        ? String(payload.body)
        : t === "prescription_resubmitted"
          ? "A patient replaced a file. Open Clinical files to verify."
          : "New patient file(s) need verification in Clinical files.",
    };
  }
  if (t === "doctor_pending_review" || t === "hospital_pending_review") {
    return {
      title,
      body: payload.body
        ? String(payload.body)
        : "Profile submitted for admin review.",
    };
  }
  return {
    title,
    body: "Open the notifications panel to read more.",
  };
}

const TOAST_DEBOUNCE_MS = 4000;
let lastToastFingerprint = "";
let lastToastAt = 0;

function shouldShowToastForPayload(payload) {
  const { title, body } = toastFromSocketPayload(payload);
  const fp = `${title}|${body}`.slice(0, 240);
  const now = Date.now();
  if (fp === lastToastFingerprint && now - lastToastAt < TOAST_DEBOUNCE_MS) {
    return null;
  }
  lastToastFingerprint = fp;
  lastToastAt = now;
  return { title, body };
}

function formatNotificationTime(createdAt) {
  if (!createdAt) return "";
  try {
    const d = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

const NAV_GROUPS = [
  {
    id: "overview",
    title: "Overview",
    items: [
      { to: "/Dashboard", label: "Dashboard", icon: FiGrid },
    ],
  },
  {
    id: "directory",
    title: "Directory",
    items: [
      { to: "/location-search", label: "Location search", icon: FiMapPin },
      { to: "/patient-list", label: "Patient List", icon: FiUsers },
      { to: "/doctor-list", label: "Doctors", icon: FiUserCheck },
      { to: "/hospital-list", label: "Hospitals", icon: FiHome },
    ],
  },
  {
    id: "clinical",
    title: "Clinical & bids",
    items: [
      { to: "/review-approval", label: "Review approval", icon: FiClipboard },
      { to: "/prescriptions-reports", label: "Clinical files", icon: FiFileText },
      { to: "/Bid", label: "Bids", icon: FiActivity },
    ],
  },
  {
    id: "patient-support",
    title: "Patient support",
    items: [
      { to: "/patient-support", label: "Live patient chat", icon: FiMessageCircle },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    items: [
      { to: "/category-management", label: "Category Management", icon: FiLayers },
      { to: "/gallery", label: "Gallery", icon: FiImage },
      { to: "/notifications", label: "Notifications", icon: FiBell },
      { to: "/settings", label: "Settings", icon: FiSettings },
    ],
  },
];

export default function Sidebar({ mobileOpen = false, onMobileClose = null }) {
  const brandLogoSrc = "/Assets/logo.png";
  const location = useLocation();
  const pathname = location.pathname;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminAccessToken = useSelector((s) => s.auth?.accessToken);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const notificationOpenRef = useRef(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [lastViewedAt, setLastViewedAt] = useState(() => {
        try {
            const s = localStorage.getItem(VIEWED_STORAGE_KEY);
            return s ? Number(s) : 0;
        } catch { return 0; }
    });
    const popupRef = useRef(null);
    const socketRef = useRef(null);
    const [badgePulse, setBadgePulse] = useState(false);
    /** Unread patient lines in live support while you are on another admin screen (Socket.IO). */
    const [supportPatientUnread, setSupportPatientUnread] = useState(0);
    const [supportBadgePulse, setSupportBadgePulse] = useState(false);

    const isActive = (path) => {
        if (path === "/Dashboard") return pathname === "/Dashboard";
        if (path === "/patient-list") return pathname === "/patient-list" || pathname.startsWith("/patient-list/");
        if (path === "/doctor-list") return pathname === "/doctor-list" || pathname.startsWith("/doctor-list/");
        if (path === "/hospital-list") return pathname === "/hospital-list" || pathname.startsWith("/hospital-list/");
        if (path === "/review-approval") return pathname === "/review-approval";
        if (path === "/Bid") return pathname === "/Bid";
        if (path === "/notifications") return pathname === "/notifications";
        if (path === "/patient-support") {
          return pathname === "/patient-support" || pathname === "/support-live";
        }
        if (path === "/category-management") return pathname === "/category-management" || pathname.startsWith("/category-management");
        if (path === "/gallery") return pathname === "/gallery";
        if (path === "/settings") return pathname === "/settings";
        if (path === "/prescriptions-reports") {
          return (
            pathname === "/prescriptions-reports" ||
            pathname === "/patient-medical-submissions"
          );
        }
        return pathname === path;
    };

  useEffect(() => {
    const p = String(pathname || "").replace(/\/$/, "") || "/";
    if (p === "/patient-support" || p === "/support-live") {
      setSupportPatientUnread(0);
    }
  }, [pathname]);

  useEffect(() => {
    if (!adminAccessToken) return undefined;
    const socket = getAdminSocket(adminAccessToken);
    if (!socket) return undefined;

    const onSupportPatientMessage = (msg) => {
      if (!msg || typeof msg !== "object") return;
      if (msg.from !== "user" || !msg.text) return;
      const p = String(pathnameRef.current || "").replace(/\/$/, "") || "/";
      if (p === "/patient-support" || p === "/support-live") return;
      setSupportPatientUnread((n) => (n >= 99 ? 99 : n + 1));
      setSupportBadgePulse(true);
      window.setTimeout(() => setSupportBadgePulse(false), 650);
      try {
        playNotificationSound();
      } catch (_) {}
    };

    socket.on("support:message", onSupportPatientMessage);
    return () => {
      try {
        socket.off("support:message", onSupportPatientMessage);
      } catch (_) {}
    };
  }, [adminAccessToken]);

  const unreadCount = notifications.filter((n) => {
    const t = n.updatedAt || n.createdAt;
    if (!t) return false;
    const ts = new Date(t).getTime();
    if (isNaN(ts)) return false;
    return ts > lastViewedAt;
  }).length;

    const markNotificationsViewed = useCallback((notificationList) => {
        const now = Date.now();
        let viewedAt = now;
        if (Array.isArray(notificationList) && notificationList.length > 0) {
            const maxTs = notificationList.reduce((max, n) => {
                const t = n.updatedAt || n.createdAt;
                if (!t) return max;
                const ts = new Date(t).getTime();
                return isNaN(ts) ? max : Math.max(max, ts);
            }, 0);
            viewedAt = Math.max(now, maxTs + 1);
        }
        setLastViewedAt(viewedAt);
        try { localStorage.setItem(VIEWED_STORAGE_KEY, String(viewedAt)); } catch (_) {}
    }, []);

    const fetchNotifications = useCallback(async (markAsViewedAfterFetch = false) => {
        setNotificationsLoading(true);
        try {
            const res = await API.get(endpoints.ADMIN_NOTIFICATIONS, {
                params: { page: 1, limit: 50 },
            });
            const data = res?.data?.data ?? res?.data ?? [];
            const list = Array.isArray(data) ? data : [];
            setNotifications(list);
            if (markAsViewedAfterFetch) markNotificationsViewed(list);
        } catch (err) {
            console.error("Fetch notifications error:", err);
            setNotifications([]);
        } finally {
            setNotificationsLoading(false);
        }
    }, [markNotificationsViewed]);

  const toggleNotificationPopup = () => {
    const next = !notificationOpen;
    setNotificationOpen(next);
    notificationOpenRef.current = next;
    if (next) fetchNotifications(true);
  };

  const closeIfMobile = () => {
    if (typeof onMobileClose === "function") {
      try {
        if (window.matchMedia("(max-width: 1100px)").matches) onMobileClose();
      } catch (_) {
        onMobileClose();
      }
    }
  };

  const openNotificationTarget = (n) => {
    if (!isAdminNotificationInteractive(n)) return;
    const path = getAdminNotificationPath(n);
    setNotificationOpen(false);
    notificationOpenRef.current = false;
    navigate(path);
    closeIfMobile();
  };

  const handleLogout = async () => {
    const ok = await showConfirm("Are you sure you want to log out?", {
      title: "Log out",
      confirmText: "Log out",
      cancelText: "Cancel",
    });
    if (!ok) return;
    closeIfMobile();
    API.post(endpoints.ADMIN_LOGOUT).catch(() => {});
    dispatch(logout());
    navigate("/");
  };

  useEffect(() => {
    if (!notificationOpen) return;
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !e.target.closest(".ring-btn")
      ) {
        setNotificationOpen(false);
        notificationOpenRef.current = false;
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [notificationOpen]);

  useEffect(() => {
    const onViewed = (e) => {
      try {
        const value =
          e?.detail != null
            ? Number(e.detail)
            : Number(localStorage.getItem(VIEWED_STORAGE_KEY));
        if (!isNaN(value)) setLastViewedAt(value);
      } catch (_) {}
    };
    window.addEventListener("healbid_notifications_viewed", onViewed);
    return () =>
      window.removeEventListener("healbid_notifications_viewed", onViewed);
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!adminAccessToken) return;
    const onGesture = () => primeNotificationAudioFromUserGesture();
    window.addEventListener("pointerdown", onGesture, true);
    window.addEventListener("keydown", onGesture, true);
    return () => {
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
    };
  }, [adminAccessToken]);

  useEffect(() => {
    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        try {
          fetchNotifications();
        } catch (_) {}
      }, 10000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const pulseBadge = () => {
      setBadgePulse(true);
      window.setTimeout(() => setBadgePulse(false), 650);
    };

    const mobileDone = () => {
      if (typeof onMobileClose === "function") {
        try {
          if (window.matchMedia("(max-width: 1100px)").matches) onMobileClose();
        } catch (_) {
          onMobileClose();
        }
      }
    };

    const buildSocketNotificationActions = (payload) => {
      const type = String(payload?.type || "").toLowerCase();
      const doctorId = payload?.doctorId ? String(payload.doctorId).trim() : "";
      const hospitalId = payload?.hospitalId ? String(payload.hospitalId).trim() : "";
      const patientId = payload?.patientId ? String(payload.patientId).trim() : "";

      if (type === "doctor_pending_review" && doctorId) {
        return [
          {
            label: "View",
            primary: false,
            onClick: ({ dismiss }) => {
              dismiss();
              navigate(`/doctor-list/${encodeURIComponent(doctorId)}`);
              mobileDone();
            },
          },
          {
            label: "Approve",
            primary: true,
            onClick: async ({ dismiss }) => {
              try {
                setGlobalLoading(true, "Approving…");
                await API.post(endpoints.adminDoctorApprove(doctorId));
                dismiss();
                showAlert("Doctor approved.", { variant: "success", title: "Approved" });
                fetchNotifications(false);
              } catch (err) {
                showAlert(err?.response?.data?.message || "Could not approve.", {
                  variant: "error",
                  title: "Approve failed",
                });
              } finally {
                setGlobalLoading(false);
              }
            },
          },
        ];
      }

      if (type === "hospital_pending_review" && hospitalId) {
        return [
          {
            label: "View",
            primary: false,
            onClick: ({ dismiss }) => {
              dismiss();
              navigate(`/hospital-list/${encodeURIComponent(hospitalId)}`);
              mobileDone();
            },
          },
          {
            label: "Approve",
            primary: true,
            onClick: async ({ dismiss }) => {
              try {
                setGlobalLoading(true, "Approving…");
                await API.post(endpoints.adminHospitalApprove(hospitalId));
                dismiss();
                showAlert("Hospital approved.", { variant: "success", title: "Approved" });
                fetchNotifications(false);
              } catch (err) {
                showAlert(err?.response?.data?.message || "Could not approve.", {
                  variant: "error",
                  title: "Approve failed",
                });
              } finally {
                setGlobalLoading(false);
              }
            },
          },
        ];
      }

      if (type === "doctor_signup" && doctorId) {
        return [
          {
            label: "View",
            primary: true,
            onClick: ({ dismiss }) => {
              dismiss();
              navigate(`/doctor-list/${encodeURIComponent(doctorId)}`);
              mobileDone();
            },
          },
        ];
      }

      if (
        (type === "prescription_upload" ||
          type === "prescription_pending" ||
          type === "prescription_resubmitted") &&
        patientId
      ) {
        const prescriptionId = String(payload?.prescriptionId ?? "").trim();
        const matchRequestId = String(payload?.matchRequestId ?? "").trim();
        const tabRaw = String(payload?.clinicalTab ?? payload?.tab ?? "").toLowerCase();
        const tab = ["prescription", "report", "insurance"].includes(tabRaw) ? tabRaw : "";
        const clinicalFilesPath = (() => {
          const qs = new URLSearchParams();
          if (matchRequestId) qs.set("highlight", `matchRequest_${matchRequestId}`);
          else if (prescriptionId) qs.set("highlight", `prescription_${prescriptionId}`);
          if (tab) qs.set("tab", tab);
          const q = qs.toString();
          return q ? `/prescriptions-reports?${q}` : "/prescriptions-reports";
        })();
        const verifyPath = prescriptionId
          ? (() => {
              const qs = new URLSearchParams();
              qs.set("prescriptionId", prescriptionId);
              qs.set("returnTo", "clinical");
              if (tab) qs.set("tab", tab);
              return `/patient-medical-submissions?${qs.toString()}`;
            })()
          : clinicalFilesPath;
        return [
          {
            label: "View",
            primary: false,
            onClick: ({ dismiss }) => {
              dismiss();
              navigate(clinicalFilesPath);
              mobileDone();
            },
          },
          {
            label: "Verify",
            primary: true,
            onClick: async ({ dismiss }) => {
              dismiss();
              try {
                setGlobalLoading(true);
                if (matchRequestId) {
                  const res = await API.post(endpoints.ADMIN_ACCEPT_PRESCRIPTIONS_FOR_MATCH_REQUEST, {
                    matchRequestId: String(matchRequestId),
                  });
                  const msg =
                    res?.data?.message ||
                    (res?.data?.data?.modifiedCount > 0
                      ? `${res.data.data.modifiedCount} file(s) verified.`
                      : "All linked files are already verified.");
                  showAlert(msg, { variant: "success", title: "Verified" });
                } else if (prescriptionId) {
                  await API.post(endpoints.adminAcceptPrescription(prescriptionId));
                  showAlert("File verified.", { variant: "success", title: "Verified" });
                } else {
                  navigate(verifyPath);
                  mobileDone();
                  return;
                }
                navigate(clinicalFilesPath);
                fetchNotifications(false);
              } catch (err) {
                showAlert(
                  err?.response?.data?.message || err?.message || "Could not verify documents.",
                  { variant: "error", title: "Verify failed" }
                );
              } finally {
                setGlobalLoading(false);
                mobileDone();
              }
            },
          },
        ];
      }

      return null;
    };

    const onRealtimeNotification = (payload) => {
      try {
        playNotificationSound();
        const actions = buildSocketNotificationActions(payload);
        if (!notificationOpenRef.current) {
          const toast = shouldShowToastForPayload(payload);
          if (toast) {
            showAlert(toast.body, {
              title: toast.title,
              variant: "info",
              duration: actions?.length ? 0 : 5600,
              actions: actions || undefined,
            });
          }
        }
        pulseBadge();
        fetchNotifications(false);
      } catch (_) {}
    };

    const onBidUpdate = () => {
      try {
        playNotificationSound();
        pulseBadge();
        fetchNotifications(false);
      } catch (_) {}
    };

    const onConnect = () => {
      stopPolling();
      try {
        fetchNotifications(false);
      } catch (_) {}
    };

    const socket = getAdminSocket(adminAccessToken);
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    socketRef.current = socket;

    socket.on("notification:new", onRealtimeNotification);
    socket.on("bid:update", onBidUpdate);
    socket.on("connect", onConnect);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);

    if (socket.connected) {
      onConnect();
    }

    return () => {
      try {
        socket.off("notification:new", onRealtimeNotification);
        socket.off("bid:update", onBidUpdate);
        socket.off("connect", onConnect);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [fetchNotifications, adminAccessToken, navigate, onMobileClose]);

  useEffect(() => {
    notificationOpenRef.current = notificationOpen;
  }, [notificationOpen]);

  return (
    <div className={`sid_barr${mobileOpen ? " sid_barr--open" : ""}`}>
      <div
        className="sid_barr__backdrop"
        role="presentation"
        onClick={() => {
          if (typeof onMobileClose === "function") onMobileClose();
        }}
      />
      <div className="sidebar">
        <div className="sidebar__sheet-grab" aria-hidden />
        <div className="sidebar__sheet-toolbar">
          <div className="sidebar__sheet-brand">
            <img
              src={brandLogoSrc}
              alt="HealBid"
              className="sidebar__sheet-brand-logo"
            />
            <a
              className="sidebar__sheet-brand-link"
              href="https://prilixlabs.com"
              target="_blank"
              rel="noreferrer"
            >
              Developed by Prilix Labs
            </a>
          </div>
          <div className="sidebar__sheet-actions">
            <div
              className="ring-btn ring-btn--toolbar"
              onClick={toggleNotificationPopup}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleNotificationPopup();
                }
              }}
              aria-label={
                unreadCount > 0
                  ? `Notifications, ${unreadCount} unread`
                  : "Notifications, no unread"
              }
            >
              <span>
                <img src={bellringing} alt="" />
              </span>
              {unreadCount > 0 && (
                <span
                  className={`notification_badge${badgePulse ? " notification_badge--pulse" : ""}`}
                  aria-hidden
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            <button
              type="button"
              className="sidebar__sheet-close"
              onClick={() => {
                if (typeof onMobileClose === "function") onMobileClose();
              }}
              aria-label="Close menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>
        <div className="logo">
          <div className="brand_lockup">
            <img src={brandLogoSrc} alt="HealBid" className="brand_logo" />
          </div>
          <div
            className="ring-btn"
            onClick={toggleNotificationPopup}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleNotificationPopup();
              }
            }}
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : "Notifications, no unread"
            }
          >
            <span>
              <img src={bellringing} alt="" />
            </span>
            {unreadCount > 0 && (
              <span
                className={`notification_badge${badgePulse ? " notification_badge--pulse" : ""}`}
                aria-hidden
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
        </div>

        {notificationOpen && (
          <div className="notification_popup" ref={popupRef}>
            <div className="notification_popup_header">
              <h3>Notifications</h3>
            </div>
            <div className="notification_popup_body">
              {notificationsLoading ? (
                <div className="notification_loading">Loading…</div>
              ) : notifications.length === 0 ? (
                <div className="notification_empty">No notifications</div>
              ) : (
                <ul className="notification_list">
                  {notifications.map((n, i) => {
                    const interactive = isAdminNotificationInteractive(n);
                    const unread = isNotificationUnread(n, lastViewedAt);
                    return (
                      <li
                        key={`${n.type}-${n.matchRequestId || n.patientId}-${i}`}
                        className={`notification_item${unread ? " notification_item--unread" : ""}${
                          interactive ? " notification_item--interactive" : ""
                        }`}
                        role={interactive ? "button" : undefined}
                        tabIndex={interactive ? 0 : undefined}
                        aria-label={
                          interactive
                            ? `${n.title || n.type}${
                                n.updatedAt || n.createdAt
                                  ? `. ${formatNotificationTime(n.updatedAt || n.createdAt)}`
                                  : ""
                              }. Press to open.`
                            : undefined
                        }
                        onClick={() => openNotificationTarget(n)}
                        onKeyDown={(e) => {
                          if (!interactive) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openNotificationTarget(n);
                          }
                        }}
                      >
                        <div className="notification_item__main">
                          <div className="notification_item__top">
                            {unread ? (
                              <span className="notification_item__dot" aria-hidden />
                            ) : (
                              <span className="notification_item__dot notification_item__dot--read" aria-hidden />
                            )}
                            <div className="notification_item_title">{n.title || n.type}</div>
                          </div>
                          <div className="notification_item_body">{n.body}</div>
                          <div className="notification_item__meta">
                            {(n.updatedAt || n.createdAt) && (
                              <span className="notification_item_time">
                                {formatNotificationTime(n.updatedAt || n.createdAt)}
                              </span>
                            )}
                            {interactive ? (
                              <span className="notification_item__cta">Open</span>
                            ) : null}
                          </div>
                        </div>
                        {interactive ? (
                          <FiChevronRight className="notification_item__chev" aria-hidden strokeWidth={2.4} />
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="menu">
          {NAV_GROUPS.map((group) => (
            <div key={group.id} className="menu_group">
              <div className="menu_group__title">{group.title}</div>
              <div className="menu_group__list">
                {group.items.map((item) => {
                  const showSupportBadge = item.to === "/patient-support" && supportPatientUnread > 0;
                  return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={`menu_item${isActive(item.to) ? " active" : ""}`}
                    onClick={closeIfMobile}
                  >
                    <span className="menu_item__icon-wrap">
                      <item.icon size={18} strokeWidth={2.1} aria-hidden />
                      {showSupportBadge ? (
                        <span
                          className={`menu_item__badge${supportBadgePulse ? " menu_item__badge--pulse" : ""}`}
                          aria-label={`${supportPatientUnread} unread patient support message${supportPatientUnread === 1 ? "" : "s"}`}
                        >
                          {supportPatientUnread > 9 ? "9+" : supportPatientUnread}
                        </span>
                      ) : null}
                    </span>
                    <span className="menu_item__label">{item.label}</span>
                  </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="bottom_section">
          <div
            className="logout"
            role="button"
            tabIndex={0}
            onClick={handleLogout}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLogout();
              }
            }}
          >
            <img src={onofbtn} alt="" />
            <span>Log Out</span>
          </div>
        </div>
      </div>
    </div>
  );
}
